import { useCallback, useEffect, useRef, useState } from "react";
import {
  ShieldCheck, Stethoscope, UserRound, Inbox as InboxIcon, SendHorizonal,
  Search, Megaphone, MessageSquare, ArrowLeft, Send, Paperclip, X, FileText, Lock,
} from "lucide-react";
import api, { API, formatApiError } from "@/lib/api";
import { toast } from "@/lib/toast";
import { useAuth } from "@/context/AuthContext";
import { useMessaging } from "@/context/MessagingContext";
import { StatusBadge } from "@/components/portal/AdminTicketInbox";
import ApptNotesModal from "@/components/portal/ApptNotesModal";

function fmtDay(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString([], { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return ""; }
}
function fmtTime(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString([], { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); }
  catch { return ""; }
}

const NOTE_WINDOW_META = {
  open: { label: "Open", cls: "open" },
  pre: { label: "Opens later", cls: "pre" },
  closed: { label: "Closed", cls: "closed" },
  unknown: { label: "Unavailable", cls: "closed" },
};
function NoteWindowBadge({ status }) {
  const m = NOTE_WINDOW_META[status] || NOTE_WINDOW_META.unknown;
  return <span className={`pmail-win ${m.cls}`} data-testid={`pmail-window-${status || "unknown"}`}>{m.label}</span>;
}

/* ----------------------- Support ticket thread (reply) ----------------------- */
function SupportThread({ conv, accent, onBack, onChanged }) {
  const { user } = useAuth();
  const { tick, refreshSummary } = useMessaging();
  const [msgs, setMsgs] = useState([]);
  const [meta, setMeta] = useState(null);
  const [text, setText] = useState("");
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const logRef = useRef(null);
  const scrollDown = () => setTimeout(() => { if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight; }, 60);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/conversations/${conv.id}/messages`);
      setMeta({ name: data.counterpart_name, ticket_code: data.conversation?.ticket_code, subject: data.conversation?.subject, status: data.conversation?.status });
      setMsgs(data.messages || []);
      scrollDown();
      refreshSummary && refreshSummary();
      onChanged && onChanged();
    } catch (e) { toast(formatApiError(e.response?.data?.detail)); }
  }, [conv.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);
  useEffect(() => { load(); }, [tick]); // eslint-disable-line react-hooks/exhaustive-deps

  const addFiles = (e) => {
    const list = Array.from(e.target.files || []);
    if (list.length) setFiles((p) => [...p, ...list]);
    e.target.value = "";
  };
  const send = async () => {
    const t = text.trim();
    if (!t && files.length === 0) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("text", t);
      files.forEach((f) => fd.append("files", f));
      const { data } = await api.post(`/conversations/${conv.id}/messages`, fd);
      setMsgs((prev) => [...prev, data.message]);
      setText(""); setFiles([]); scrollDown();
      onChanged && onChanged();
    } catch (e) { toast(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="inbox-thread pmail-thread" style={{ "--ib-accent": accent }} data-testid="pmail-support-thread">
      <div className="ib-thread-head">
        <button className="ib-iconbtn" onClick={onBack} data-testid="pmail-thread-back"><ArrowLeft size={16} /></button>
        <span className="ib-avatar admin"><ShieldCheck size={16} /></span>
        <div className="ib-thread-name">
          <b data-testid="pmail-thread-name">{meta?.name || conv.counterpart_name || "Support"}</b>
          <span className="ib-ticket-chip">
            <span className="tkt-code">{meta?.ticket_code || conv.ticket_code}</span>
            <StatusBadge status={meta?.status || conv.status} />
            {(meta?.subject || conv.subject) ? ` · ${meta?.subject || conv.subject}` : ""}
          </span>
        </div>
      </div>

      <div className="ib-log" ref={logRef} data-testid="pmail-thread-messages">
        {msgs.length === 0 && <p className="ib-log-empty">No messages in this ticket yet.</p>}
        {msgs.map((m) => {
          const me = m.sender_id === user.id;
          return (
            <div className={`ib-bubble ${me ? "me" : "them"}`} key={m.id}>
              {!me && <span className="ib-who">{m.sender_name}</span>}
              {m.text && <span className="ib-text">{m.text}</span>}
              {(m.files || []).map((f, i) => (
                <a key={i} className="ib-file" href={`${API}/files/${f.file_id}`} target="_blank" rel="noreferrer">
                  <FileText size={14} /> {f.filename}
                </a>
              ))}
              <span className="ib-time">{fmtTime(m.created_at)}</span>
            </div>
          );
        })}
      </div>

      {files.length > 0 && (
        <div className="ib-attachbar">
          {files.map((f, i) => (
            <span className="ib-chip" key={i}><FileText size={12} /> {f.name}
              <button onClick={() => setFiles(files.filter((_, j) => j !== i))}><X size={12} /></button>
            </span>
          ))}
        </div>
      )}

      <div className="ib-composer">
        <label className="ib-iconbtn" title="Attach files">
          <Paperclip size={18} />
          <input type="file" hidden multiple onChange={addFiles} />
        </label>
        <input
          className="ib-input"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && (e.preventDefault(), send())}
          placeholder="Reply to the support team…"
          data-testid="pmail-thread-input"
        />
        <button className="ib-send" onClick={send} disabled={busy} data-testid="pmail-thread-send"><Send size={16} /></button>
      </div>
    </div>
  );
}

/* --------------------------- Announcement detail (read-only) --------------------------- */
function AnnouncementDetail({ mail, accent, onBack }) {
  return (
    <div className="pmail-announce" data-testid="pmail-announcement-detail">
      <button className="ib-iconbtn pmail-announce-back" onClick={onBack} data-testid="pmail-announce-back"><ArrowLeft size={16} /></button>
      <div className="pmail-announce-head">
        <span className="ib-avatar admin"><Megaphone size={16} style={{ color: accent }} /></span>
        <div>
          <b>{mail.subject || "(no subject)"}</b>
          <small>{mail.audience || "Announcement"} · {fmtTime(mail.created_at)}</small>
        </div>
      </div>
      <div className="pmail-announce-body">{mail.body}</div>
    </div>
  );
}

/**
 * Categorised mailbox for patient & doctor portals (mirrors the admin Mail inbox).
 * Categories:
 *   - Support : conversations with the support/admin team (ticket system) + announcements
 *   - Doctor (patient) / Patient (doctor) : per-appointment private notes channel
 * Each category has Inbox (received) + Sent boxes.
 */
export default function PortalMail({ role = "patient", accent = "#185FA5" }) {
  const { tick, refreshSummary } = useMessaging();
  const peerKey = role === "patient" ? "doctor" : "patient";
  const peerLabel = role === "patient" ? "Doctor" : "Patient";
  const PeerIcon = role === "patient" ? Stethoscope : UserRound;

  const [category, setCategory] = useState("support"); // 'support' | peerKey
  const [box, setBox] = useState("inbox");              // 'inbox' | 'sent'
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTicket, setActiveTicket] = useState(null);
  const [activeMail, setActiveMail] = useState(null);
  const [apptModal, setApptModal] = useState(null);
  const reqRef = useRef(0);

  const load = useCallback(async () => {
    const myReq = ++reqRef.current;
    setLoading(true);
    let result = [];
    try {
      if (category === "support") {
        if (box === "inbox") {
          const [tk, ml] = await Promise.all([
            api.get("/my-tickets", { params: { box: "inbox" } }),
            api.get("/mail/me").catch(() => ({ data: { mail: [] } })),
          ]);
          const tickets = (tk.data.tickets || []).map((t) => ({ ...t, _type: "ticket" }));
          const mails = (ml.data.mail || []).map((m) => ({ ...m, _type: "announcement" }));
          result = [...tickets, ...mails].sort((a, b) => String(b.last_at || b.created_at || "").localeCompare(String(a.last_at || a.created_at || "")));
        } else {
          const tk = await api.get("/my-tickets", { params: { box: "sent" } });
          result = (tk.data.tickets || []).map((t) => ({ ...t, _type: "ticket" }));
        }
      } else {
        const r = await api.get("/my-appt-note-threads", { params: { box } });
        result = (r.data.threads || []).map((t) => ({ ...t, _type: "appt" }));
      }
    } catch { result = []; }
    // Guard against out-of-order responses overwriting the latest selection.
    if (myReq === reqRef.current) {
      setItems(result);
      setLoading(false);
    }
  }, [category, box]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { load(); }, [tick]); // eslint-disable-line react-hooks/exhaustive-deps

  const ql = q.trim().toLowerCase();
  const visible = !ql ? items : items.filter((it) => {
    if (it._type === "ticket") return [it.subject, it.ticket_code, it.last_text].join(" ").toLowerCase().includes(ql);
    if (it._type === "announcement") return [it.subject, it.body].join(" ").toLowerCase().includes(ql);
    return [it.other_name, it.specialty, it.last_text].join(" ").toLowerCase().includes(ql);
  });

  // ----- detail views -----
  if (activeTicket) {
    return <SupportThread conv={activeTicket} accent={accent} onBack={() => { setActiveTicket(null); load(); }} onChanged={load} />;
  }
  if (activeMail) {
    return <AnnouncementDetail mail={activeMail} accent={accent} onBack={() => setActiveMail(null)} />;
  }

  const emptyText = category === "support"
    ? (box === "inbox" ? "No messages from the support team yet." : "You haven't sent any support messages yet.")
    : (box === "inbox"
        ? `No messages received from a ${peerLabel.toLowerCase()} yet.`
        : `You haven't messaged a ${peerLabel.toLowerCase()} yet.`);
  const placeholder = category === "support"
    ? "Search by subject, ticket # or text…"
    : `Search by ${peerLabel.toLowerCase()} name, specialty or text…`;

  return (
    <div className="panel pmail" data-testid="portal-mail">
      <div className="pmail-head">
        <h3><InboxIcon size={18} style={{ color: accent }} /> Mail</h3>
        <p className="sub">Your conversations, organised by category. Doctor &amp; patient messages are private to your shared appointment.</p>
      </div>

      {/* category tabs */}
      <div className="pmail-cats" data-testid="pmail-categories">
        <button className={`pmail-cat ${category === "support" ? "on" : ""}`} onClick={() => { setCategory("support"); setActiveTicket(null); setActiveMail(null); }} data-testid="pmail-cat-support">
          <ShieldCheck size={15} /> Support
        </button>
        <button className={`pmail-cat ${category === peerKey ? "on" : ""}`} onClick={() => { setCategory(peerKey); setActiveTicket(null); setActiveMail(null); }} data-testid={`pmail-cat-${peerKey}`}>
          <PeerIcon size={15} /> {peerLabel}
        </button>
      </div>

      {/* box tabs */}
      <div className="pmail-boxes" data-testid="pmail-boxes">
        <button className={`pmail-box ${box === "inbox" ? "on" : ""}`} onClick={() => setBox("inbox")} data-testid="pmail-box-inbox">
          <InboxIcon size={14} /> Inbox
        </button>
        <button className={`pmail-box ${box === "sent" ? "on" : ""}`} onClick={() => setBox("sent")} data-testid="pmail-box-sent">
          <SendHorizonal size={14} /> Sent
        </button>
      </div>

      <div className="pmail-search">
        <Search size={15} className="pmail-search-ic" />
        <input className="pmail-search-input" placeholder={placeholder} value={q} onChange={(e) => setQ(e.target.value)} data-testid="pmail-search-input" />
        {q && <button className="pmail-search-clear" onClick={() => setQ("")} data-testid="pmail-search-clear">✕</button>}
      </div>

      <div className="pmail-list" data-testid="pmail-list">
        {loading && <p className="pmail-empty">Loading…</p>}
        {!loading && visible.length === 0 && (
          <p className="pmail-empty" data-testid="pmail-empty"><MessageSquare size={26} /><br />{ql ? `No results for “${q}”.` : emptyText}</p>
        )}

        {!loading && visible.map((it) => {
          if (it._type === "ticket") {
            return (
              <button key={`t-${it.id}`} className={`pmail-row ${it.unread > 0 ? "unread" : ""}`} onClick={() => setActiveTicket(it)} data-testid={`pmail-row-ticket-${it.id}`}>
                <span className="ib-avatar admin"><ShieldCheck size={16} /></span>
                <span className="pmail-row-main">
                  <span className="pmail-row-top">
                    <span className="tkt-code">{it.ticket_code}</span>
                    <b className="pmail-row-name">Support team</b>
                    <time>{fmtDay(it.last_at)}</time>
                  </span>
                  <span className="pmail-row-sub">{it.subject || "General enquiry"} <StatusBadge status={it.status} /></span>
                  <span className="pmail-row-prev">{it.last_text || "No messages yet"}</span>
                </span>
                {it.unread > 0 && <span className="ib-unread" data-testid={`pmail-unread-${it.id}`}>{it.unread}</span>}
              </button>
            );
          }
          if (it._type === "announcement") {
            return (
              <button key={`a-${it.id}`} className="pmail-row" onClick={() => setActiveMail(it)} data-testid={`pmail-row-announce-${it.id}`}>
                <span className="ib-avatar admin"><Megaphone size={16} /></span>
                <span className="pmail-row-main">
                  <span className="pmail-row-top">
                    <span className="pmail-tag">Announcement</span>
                    <b className="pmail-row-name">{it.subject || "(no subject)"}</b>
                    <time>{fmtDay(it.created_at)}</time>
                  </span>
                  <span className="pmail-row-sub">{it.audience || "Broadcast"}</span>
                  <span className="pmail-row-prev">{(it.body || "").slice(0, 90)}</span>
                </span>
              </button>
            );
          }
          // appt private-notes thread
          return (
            <button key={`p-${it.appointment_id}`} className={`pmail-row ${it.unread > 0 ? "unread" : ""}`} onClick={() => setApptModal(it)} data-testid={`pmail-row-appt-${it.appointment_id}`}>
              <span className={`ib-avatar ${peerKey}`}><Lock size={15} /></span>
              <span className="pmail-row-main">
                <span className="pmail-row-top">
                  <b className="pmail-row-name">{it.other_name}</b>
                  <time>{fmtDay(it.last_at)}</time>
                </span>
                <span className="pmail-row-sub">
                  {it.specialty ? `${it.specialty} · ` : ""}{it.date} {it.time} <NoteWindowBadge status={it.window_status} />
                </span>
                <span className="pmail-row-prev">{it.last_text || "No messages yet"}</span>
              </span>
              {it.unread > 0 && <span className="ib-unread" data-testid={`pmail-unread-appt-${it.appointment_id}`}>{it.unread}</span>}
            </button>
          );
        })}
      </div>

      {apptModal && (
        <ApptNotesModal
          appt={{ id: apptModal.appointment_id, date: apptModal.date, time: apptModal.time, specialty: apptModal.specialty }}
          role={role}
          accent={accent}
          onClose={() => { setApptModal(null); refreshSummary && refreshSummary(); load(); }}
        />
      )}
    </div>
  );
}
